import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";

const API_KEY = "62bf5b9daecb7ab31862dd1a1d0a3773";
const API_URL = `https://api.themoviedb.org/3/movie/now_playing?api_key=${API_KEY}&language=en-US&region=IN&page=`;

// Language Config
const LANG_CONFIG = {
  hindi: { code: "hi", label: "Hindi", accent: "#FF6B35" },
  tamil: { code: "ta", label: "Tamil", accent: "#E63946" },
  telugu: { code: "te", label: "Telugu", accent: "#F4A261" },
  malayalam: { code: "ml", label: "Malayalam", accent: "#2A9D8F" },
  kannada: { code: "kn", label: "Kannada", accent: "#E9C46A" },
  bengali: { code: "bn", label: "Bengali", accent: "#A8DADC" },
  marathi: { code: "mr", label: "Marathi", accent: "#F72585" },
  gujarati: { code: "gu", label: "Gujarati", accent: "#7209B7" },
  punjabi: { code: "pa", label: "Punjabi", accent: "#4CC9F0" },
  english: { code: "en", label: "English", accent: "#1D3557" },
};

// Score Ring
const ScoreRing = ({ score }) => {
  const pct = Math.round(((score || 0) / 10) * 100);
  const color = score >= 7 ? "#4ade80" : score >= 5 ? "#fbbf24" : "#f87171";

  return (
    <svg width="34" height="34">
      <circle cx="17" cy="17" r="14" fill="rgba(0,0,0,0.6)" />
      <circle
        cx="17"
        cy="17"
        r="14"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeDasharray={`${(pct / 100) * 87.96} 87.96`}
        transform="rotate(-90 17 17)"
      />
      <text x="17" y="21" textAnchor="middle" fontSize="8.5" fill="white">
        {score?.toFixed(1)}
      </text>
    </svg>
  );
};

// Movie Card
const MovieCard = ({ movie, accent }) => {
  return (
    <div style={{ flex: "0 0 150px" }}>
      <a
        href={`https://www.google.com/search?q=${encodeURIComponent(
          `${movie.title} near me showtimes`
        )}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none" }}
      >
        <div
          style={{
            borderRadius: "10px",
            overflow: "hidden",
            cursor: "pointer",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <img
            src={`https://image.tmdb.org/t/p/w342${movie.poster_path}`}
            alt={movie.title}
            style={{ width: "100%", height: "225px", objectFit: "cover" }}
          />
          <div style={{ padding: "8px", background: "#0b0b18" }}>
            <p
              style={{
                color: "#fff",
                fontSize: "12px",
                margin: 0,
                fontWeight: "500",
                lineHeight: "1.3",
              }}
            >
              {movie.title.length > 25
                ? `${movie.title.substring(0, 25)}...`
                : movie.title}
            </p>
            <small style={{ color: "#aaa", fontSize: "10px" }}>
              ⭐ {movie.vote_average?.toFixed(1)}
            </small>
          </div>
        </div>
      </a>
    </div>
  );
};

// Single Row Section
const LanguageSection = ({ movies, config }) => {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir * 400,
      behavior: "smooth",
    });
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ color: config.accent }}>
          {config.emoji} {config.label} Movies
        </h3>

        <div>
          <button onClick={() => scroll(-1)}>‹</button>
          <button onClick={() => scroll(1)}>›</button>
        </div>
      </div>

      {/* Row */}
      <div
        ref={scrollRef}
        style={{
          display: "flex",
          gap: "10px",
          overflowX: "auto",
        }}
      >
        {movies.map((m) => (
          <MovieCard key={m.id} movie={m} accent={config.accent} />
        ))}
      </div>
    </div>
  );
};

// Sort
function sortMovies(movies, key) {
  const arr = [...movies];
  if (key === "rating")
    return arr.sort((a, b) => b.vote_average - a.vote_average);
  if (key === "new")
    return arr.sort(
      (a, b) => new Date(b.release_date) - new Date(a.release_date)
    );
  return arr;
}

// MAIN
const Movies = () => {
  const [allMovies, setAllMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState("tamil");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("default");

  useEffect(() => {
    const fetchMovies = async () => {
      let all = [];
      for (let i = 1; i <= 3; i++) {
        const res = await fetch(`${API_URL}${i}`);
        const data = await res.json();
        all = [...all, ...data.results];
      }
      setAllMovies(all);
      setLoading(false);
    };
    fetchMovies();
  }, []);

  const filtered = allMovies.filter((m) => {
    if (search && !m.title.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  const currentConfig = LANG_CONFIG[activeLang];

  const currentMovies = sortMovies(
    filtered.filter((m) => m.original_language === currentConfig.code),
    sortKey
  );

  return (
    <div style={{ padding: "20px", background: "#080812", minHeight: "100vh" }}>
      {/* Responsive CSS */}
      <style>{`
        .lang-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          margin-bottom: 20px;
        }
        @media (min-width: 768px) {
          .lang-grid {
            grid-template-columns: repeat(${
              Object.keys(LANG_CONFIG).length
            }, 1fr);
          }
        }
        .lang-btn {
          padding: 10px 12px;
          color: #fff;
          border: none;
          border-radius: 5px;
          width: 100%;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: opacity 0.2s;
        }
        .lang-btn:hover {
          opacity: 0.85;
        }
      `}</style>

      {/* Language Selector */}
      <div className="lang-grid">
        {Object.keys(LANG_CONFIG).map((lang) => (
          <button
            key={lang}
            onClick={() => setActiveLang(lang)}
            className="lang-btn"
            style={{
              background:
                activeLang === lang ? LANG_CONFIG[lang].accent : "#222",
            }}
          >
            {LANG_CONFIG[lang].label}
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        placeholder="Search movie..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          padding: "8px",
          marginBottom: "20px",
          width: "100%",
          boxSizing: "border-box",
          background: "#1a1a2e",
          border: "1px solid #333",
          borderRadius: "5px",
          color: "#fff",
        }}
      />

      {/* Content */}
      {loading ? (
        <p style={{ color: "#fff" }}>Loading...</p>
      ) : (
        <LanguageSection movies={currentMovies} config={currentConfig} />
      )}
    </div>
  );
};

export default Movies;
